<template>
  <span v-if="c" class="cat-chip" :class="[`cat-chip--${c.category}`, { 'cat-chip--lg': large }]" :title="title">
    <span class="cat-dot" :class="{ 'cat-dot--live': live }"></span>
    <i :class="icon" class="cat-icon"></i>
    <span class="cat-label">{{ c.label }}</span>
    <span v-if="showSource" class="cat-source">{{ c.source === "manual" ? "set by hand" : "auto" }}</span>
  </span>
</template>

<script>
/** Category of a tracked disease (classification from GET diseases or diseases/{id}/summary). */
const ICONS = {
  sporadic: "fe fe-shuffle", endemic: "fe fe-repeat", hyperendemic: "fe fe-layers", outbreak: "fe fe-alert-triangle",
  epidemic: "fe fe-alert-octagon", pandemic: "fe fe-globe", none: "fe fe-check-circle",
};

export default {
  props: {
    classification: { type: Object, default: null },
    large: { type: Boolean, default: false },
    showSource: { type: Boolean, default: false },
  },
  computed: {
    c() { return this.classification; },
    icon() { return ICONS[this.c.category] || "fe fe-tag"; },
    live() { return ["outbreak", "epidemic", "pandemic"].includes(this.c.category); },
    title() {
      const auto = this.c.source === "manual" && this.c.automatic !== this.c.category ? ` · The data alone says: ${this.c.automatic_label}.` : "";
      return `${this.c.description} ${this.c.reason || ""}${auto}`.trim();
    },
  },
};
</script>

<style scoped>
.cat-chip {
  --fg: #475569; --bg: #f1f5f9; --ring: #cbd5e1; --dot: #94a3b8;
  display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px 3px 8px; border-radius: 999px;
  font-size: 0.72rem; font-weight: 700; letter-spacing: 0.02em; line-height: 1.5; white-space: nowrap;
  color: var(--fg); background: var(--bg); box-shadow: inset 0 0 0 1px var(--ring);
}
.cat-chip--lg { font-size: 0.85rem; padding: 6px 14px 6px 10px; gap: 8px; box-shadow: inset 0 0 0 1px var(--ring), 0 4px 14px rgba(15, 23, 42, 0.18); background: #fff; }
.cat-chip--sporadic { --fg: #0f766e; --bg: #f0fdfa; --ring: #99f6e4; --dot: #14b8a6; }
.cat-chip--endemic { --fg: #1d4ed8; --bg: #eff6ff; --ring: #bfdbfe; --dot: #3b82f6; }
.cat-chip--hyperendemic { --fg: #6d28d9; --bg: #f5f3ff; --ring: #ddd6fe; --dot: #8b5cf6; }
.cat-chip--outbreak { --fg: #b45309; --bg: #fffbeb; --ring: #fde68a; --dot: #f59e0b; }
.cat-chip--epidemic { --fg: #b91c1c; --bg: #fef2f2; --ring: #fecaca; --dot: #ef4444; }
.cat-chip--pandemic { --fg: #881337; --bg: #fff1f2; --ring: #fda4af; --dot: #be123c; }
.cat-chip--none { --fg: #475569; --bg: #f8fafc; --ring: #e2e8f0; --dot: #94a3b8; }
.cat-dot { position: relative; width: 7px; height: 7px; border-radius: 50%; background: var(--dot); flex-shrink: 0; }
.cat-dot--live::after { content: ""; position: absolute; inset: -4px; border-radius: 50%; border: 2px solid var(--dot); opacity: 0; animation: cat-pulse 1.8s ease-out infinite; }
.cat-icon { font-size: 0.95em; }
.cat-source { font-weight: 600; opacity: 0.7; padding-left: 6px; margin-left: 2px; border-left: 1px solid var(--ring); text-transform: none; }
@keyframes cat-pulse { 0% { transform: scale(0.6); opacity: 0.9; } 100% { transform: scale(1.6); opacity: 0; } }
@media (prefers-reduced-motion: reduce) { .cat-dot--live::after { animation: none; } }
</style>
